import { useEffect, useState } from 'react';
import { YANDEX_MAPS_API_KEY } from 'app/config/envVariables';
import { YANDEX_MAPS_API_VERSION, YANDEX_MAPS_LANG } from '../constants';

declare global {
  interface Window {
    ymaps: unknown;
  }
}

// Global state to track script loading
let scriptLoadingPromise: Promise<void> | null = null;
let scriptLoaded = false;
let scriptError: string | null = null;

const loadYandexMapsScript = async (): Promise<void> => {
  // If already loaded, return resolved promise
  if (scriptLoaded && window.ymaps) {
    return;
  }

  // If there's an error, return rejected promise
  if (scriptError) {
    throw new Error(scriptError);
  }

  // If already loading, return the existing promise
  if (scriptLoadingPromise) {
    // eslint-disable-next-line @typescript-eslint/return-await
    return scriptLoadingPromise;
  }

  // Check if script tag already exists
  const existingScript = document.querySelector('script[src*="api-maps.yandex.ru"]');
  if (existingScript) {
    // Script exists, wait for it to be ready
    scriptLoadingPromise = new Promise<void>((resolve, reject) => {
      const checkReady = (): void => {
        if (window.ymaps && typeof window.ymaps === 'object' && 'ready' in window.ymaps) {
          (window.ymaps as any).ready(() => {
            scriptLoaded = true;
            scriptLoadingPromise = null;
            resolve();
          });
        } else {
          // Wait a bit and check again
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    });
    // eslint-disable-next-line @typescript-eslint/return-await
    return scriptLoadingPromise;
  }

  // Start loading the script
  scriptLoadingPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://api-maps.yandex.ru/${YANDEX_MAPS_API_VERSION}/?apikey=${YANDEX_MAPS_API_KEY}&lang=${YANDEX_MAPS_LANG}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      if (window.ymaps && typeof window.ymaps === 'object' && 'ready' in window.ymaps) {
        (window.ymaps as any).ready(() => {
          scriptLoaded = true;
          scriptLoadingPromise = null;
          resolve();
        });
      } else {
        scriptError = 'Yandex Maps API not properly initialized';
        scriptLoadingPromise = null;
        reject(new Error(scriptError));
      }
    };

    script.onerror = () => {
      scriptError = 'Failed to load Yandex Maps API';
      scriptLoadingPromise = null;
      reject(new Error(scriptError));
    };

    document.head.appendChild(script);
  });

  // eslint-disable-next-line @typescript-eslint/return-await
  return scriptLoadingPromise;
};

export const useYandexMapsApi = (): {
  isLoaded: boolean;
  error: string | null;
  ymaps: unknown;
} => {
  const [isLoaded, setIsLoaded] = useState(scriptLoaded);
  const [error, setError] = useState<string | null>(scriptError);

  useEffect(() => {
    // If already loaded, no need to do anything
    if (scriptLoaded && window.ymaps) {
      setIsLoaded(true);
      return;
    }

    // If there's an error, show it
    if (scriptError) {
      setError(scriptError);
      return;
    }

    // Load the script
    loadYandexMapsScript()
      .then(() => {
        setIsLoaded(true);
        setError(null);
      })
      .catch((err) => {
        setError(err.message);
      });
  }, []);

  return { isLoaded, error, ymaps: window.ymaps };
};
